import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  BadgeVariants,
  RowActionsMenu,
} from '@carboledger/ui';
import { MoreVertical } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { ROUTES } from '@/app/routes';
import { LoadingOverlay, EmptyState, ConfirmDialog } from '@/components/feedback';
import {
  AddAttachmentModal,
  AttachmentsTabView,
  DiscrepancyModal,
  DetailPageHeader,
  LinkContractsForShipment,
  buildShipmentDetailLinkFields,
} from '@/components/common';
import { ATTACHMENT_TARGETS } from '@/lib/api/attachmentsApi';
import {
  useGetShipmentDetailQuery,
  useDeleteIncomingShipmentMutation,
} from '../api/incomingShipmentsApi';
import {
  buildShipmentMenuActions,
  resolveShipmentMenuItems,
} from '../constants/incomingShipmentActions';
import { ShipmentOverviewTabView } from '../components/ShipmentOverviewTabView';
import { SHIPMENT_STATUS_TONE } from '@/config/statusTones';
import { buildShipmentDiscrepancySections } from '../constants/incomingShipmentDiscrepancy';
import { LinkedContractsTabView } from '../components/LinkedContractsTabView';
import { LinkedDeclarationsTabView } from '../components/LinkedDeclarationsTabView';

import styles from './ShipmentDetailPage.module.scss';

const DetailTab = Object.freeze({
  overview: 'overview',
  linkedContracts: 'linkedContracts',
  linkedDeclarations: 'linkedDeclarations',
  attachments: 'attachments',
});

// Inbounds ▸ Incoming Shipments ▸ Shipment details.
export const ShipmentDetailPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, shipmentId } = useParams();
  const [activeTab, setActiveTab] = useState(DetailTab.overview);
  const [isDiscrepancyOpen, setIsDiscrepancyOpen] = useState(false);
  const [isLinkContractsOpen, setIsLinkContractsOpen] = useState(false);
  const [isAttachmentOpen, setIsAttachmentOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteIncomingShipment, { isLoading: isDeleting }] = useDeleteIncomingShipmentMutation();

  const {
    data: shipmentDetail,
    isLoading: isLoadingShipment,
    isError: isShipmentError,
  } = useGetShipmentDetailQuery(shipmentId);

  const tabs = useMemo(
    () => [
      { value: DetailTab.overview, label: t('SECTION_OVERVIEW') },
      {
        value: DetailTab.linkedContracts,
        label: t('INCOMING_SHIPMENTS_DETAIL_TAB_LINKED_CONTRACTS'),
      },
      {
        value: DetailTab.linkedDeclarations,
        label: t('INCOMING_SHIPMENTS_DETAIL_TAB_LINKED_DECLARATIONS'),
      },
      { value: DetailTab.attachments, label: t('SECTION_ATTACHMENTS') },
    ],
    [t],
  );

  const goToShipments = useCallback(
    () => navigate(ROUTES.incomingShipments(module)),
    [navigate, module],
  );

  const breadcrumbItems = useMemo(
    () => [
      {
        label: t('INCOMING_SHIPMENTS_TITLE'),
        onClick: () => navigate(ROUTES.incomingShipments(module)),
      },
      { label: shipmentDetail?.shipmentId ?? '-' },
    ],
    [t, navigate, module, shipmentDetail?.shipmentId],
  );

  const handleLinkContract = useCallback(() => setIsLinkContractsOpen(true), []);
  const handleCloseLinkContracts = useCallback(() => setIsLinkContractsOpen(false), []);
  const handleAddDeclarations = useCallback(() => {
    navigate(ROUTES.incomingShipmentAddDeclarations(module, shipmentId));
  }, [navigate, module, shipmentId]);
  const handleAddAttachment = useCallback(() => setIsAttachmentOpen(true), []);
  const handleCloseAttachment = useCallback(() => setIsAttachmentOpen(false), []);
  const handleEdit = useCallback(() => {
    navigate(ROUTES.incomingShipmentEdit(module, shipmentId));
  }, [navigate, module, shipmentId]);
  const handleDelete = useCallback(() => setIsDeleteOpen(true), []);
  const handleCancelDelete = useCallback(() => setIsDeleteOpen(false), []);
  const handleConfirmDelete = useCallback(async () => {
    if (isDeleting) return;
    const result = await deleteIncomingShipment({ module, id: shipmentId });
    setIsDeleteOpen(false);
    // The shipment is gone, so its detail route is too.
    if (!result.error) navigate(ROUTES.incomingShipments(module));
  }, [isDeleting, deleteIncomingShipment, navigate, module, shipmentId]);

  const menuItems = useMemo(
    () =>
      resolveShipmentMenuItems(
        buildShipmentMenuActions(t, {
          onAddAttachment: handleAddAttachment,
          onEdit: handleEdit,
          onDelete: handleDelete,
        }),
        shipmentDetail,
      ),
    [t, handleAddAttachment, handleEdit, handleDelete, shipmentDetail],
  );
  const linkDetailFields = useCallback(
    (unitType) => buildShipmentDetailLinkFields(t, shipmentDetail, unitType),
    [t, shipmentDetail],
  );

  const linkUnassignedByUnit = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(shipmentDetail?.rawQuantities ?? {}).map(([unitType, raw]) => [
          unitType,
          raw?.unassigned ?? null,
        ]),
      ),
    [shipmentDetail],
  );

  const handleOpenDiscrepancies = useCallback(() => setIsDiscrepancyOpen(true), []);
  const handleCloseDiscrepancies = useCallback(() => setIsDiscrepancyOpen(false), []);

  const metaFields = useMemo(() => {
    if (!shipmentDetail) return [];
    return [
      {
        id: 'material',
        label: t('FIELD_MATERIAL_NAME'),
        value: shipmentDetail.materialName,
      },
      {
        id: 'sentBy',
        label: t('FIELD_SENT_BY'),
        value: shipmentDetail.supplierName,
      },
      {
        id: 'receivedAt',
        label: t('FIELD_RECEIVED_AT'),
        value: shipmentDetail.facilityName,
      },
      {
        id: 'terminal',
        label: t('FIELD_TERMINAL_NAME'),
        value: shipmentDetail.terminal,
      },
      {
        id: 'status',
        label: t('FIELD_STATUS'),
        value: shipmentDetail.status,
        type: 'status',
        tone: SHIPMENT_STATUS_TONE[shipmentDetail.statusCode] ?? BadgeVariants.default,
      },
      {
        id: 'dateOfDischarge',
        label: t('FIELD_DATE_OF_DISCHARGE'),
        value: shipmentDetail.dateOfDischarge,
      },
      {
        id: 'dateOfDispatch',
        label: t('FIELD_DATE_OF_DISPATCH'),
        value: shipmentDetail.dateOfDispatch,
      },
      {
        id: 'daysLeft',
        label: t('FIELD_DAYS_LEFT'),
        value:
          shipmentDetail.daysLeft == null
            ? '-'
            : t('INCOMING_SHIPMENTS_DAYS_LEFT', { days: shipmentDetail.daysLeft }),
      },
      {
        id: 'discrepancies',
        label: t('FIELD_DISCREPANCIES'),
        count: shipmentDetail.discrepancyCount,
        type: 'discrepancy',
        ariaLabel: t('ACTION_VIEW_DISCREPANCIES'),
        onClick: handleOpenDiscrepancies,
      },
    ];
  }, [t, shipmentDetail, handleOpenDiscrepancies]);

  const discrepancySections = useMemo(
    () => buildShipmentDiscrepancySections(t, shipmentDetail),
    [t, shipmentDetail],
  );

  if (isLoadingShipment) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isShipmentError || !shipmentDetail) {
    return (
      <EmptyState
        title={t('INCOMING_SHIPMENTS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToShipments}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <div className={styles.page}>
      <DetailPageHeader
        breadcrumbItems={breadcrumbItems}
        title={shipmentDetail.shipmentId}
        actions={
          <>
            <Button
              variant={ButtonVariants.secondary}
              size={ButtonSizes.sm}
              onClick={handleLinkContract}
            >
              {t('ACTION_LINK_CONTRACT')}
            </Button>
            <Button
              variant={ButtonVariants.primary}
              size={ButtonSizes.sm}
              onClick={handleAddDeclarations}
            >
              {t('ACTION_ADD_DECLARATIONS')}
            </Button>
            <RowActionsMenu
              items={menuItems}
              ariaLabel={t('COMMON_MORE_ACTIONS')}
              icon={MoreVertical}
            />
          </>
        }
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsAriaLabel={t('INCOMING_SHIPMENTS_TITLE')}
      />

      <div className={styles.content}>
        {activeTab === DetailTab.overview && <ShipmentOverviewTabView shipment={shipmentDetail} />}

        {activeTab === DetailTab.linkedContracts && (
          <LinkedContractsTabView
            module={module}
            shipmentId={shipmentId}
            shipment={shipmentDetail}
            onLinkContract={handleLinkContract}
          />
        )}

        {activeTab === DetailTab.linkedDeclarations && (
          <LinkedDeclarationsTabView
            module={module}
            shipmentId={shipmentId}
            shipment={shipmentDetail}
            onAddDeclarations={handleAddDeclarations}
          />
        )}

        {activeTab === DetailTab.attachments && (
          <AttachmentsTabView
            attachments={shipmentDetail.attachments}
            target={ATTACHMENT_TARGETS.shipment}
            module={module}
            entityId={shipmentId}
            modalTitle={t('INCOMING_SHIPMENTS_ADD_ATTACHMENT_MODAL_TITLE', {
              shipmentId: shipmentDetail.shipmentId,
            })}
            addLabel={t('ACTION_ADD_ATTACHMENT')}
          />
        )}
      </div>

      <LinkContractsForShipment
        module={module}
        shipmentDbId={shipmentId}
        shipmentLabel={shipmentDetail.shipmentId}
        detailFields={linkDetailFields}
        unassignedByUnit={linkUnassignedByUnit}
        open={isLinkContractsOpen}
        onClose={handleCloseLinkContracts}
      />

      <DiscrepancyModal
        open={isDiscrepancyOpen}
        onClose={handleCloseDiscrepancies}
        referenceId={shipmentDetail.shipmentId}
        discrepancies={shipmentDetail.discrepancies}
        sections={discrepancySections}
      />

      <AddAttachmentModal
        open={isAttachmentOpen}
        onClose={handleCloseAttachment}
        title={t('INCOMING_SHIPMENTS_ADD_ATTACHMENT_MODAL_TITLE', {
          shipmentId: shipmentDetail.shipmentId,
        })}
        target={ATTACHMENT_TARGETS.shipment}
        module={module}
        entityId={shipmentId}
      />

      <ConfirmDialog
        open={isDeleteOpen}
        title={t('INCOMING_SHIPMENTS_DELETE_TITLE')}
        bodyTitle={t('INCOMING_SHIPMENTS_DELETE_BODY_TITLE', {
          shipmentId: shipmentDetail.shipmentId,
        })}
        description={t('INCOMING_SHIPMENTS_DELETE_DESCRIPTION', {
          materialName: shipmentDetail.materialName,
          supplierName: shipmentDetail.supplierName,
        })}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
